import React from "react";
import { useCurrencySymbol, useUserV2 } from "hooks";
import { breakpointsInPx } from "lib/checkViewport";
import { useCampaignThresholds } from "packages/membership/components/RedeemedBenefits/ActiveBenefits/FreeShippingCard/hooks/useCampaignThresholds";
import { useEntitlements } from "packages/membership/pages/MemberBenefits/useEntitlements";
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";
import { Carousel } from "packages/ui-shared/components/Carousel/Carousel";
import { SwiperSlide } from "swiper/react";
import {
  useTrackElementClicked,
  useTrackElementViewed
} from "packages/membership/tracking";
import {
  activeBenefitsCardImageStyle,
  bodyDescription,
  bodyTitle,
  buttonsWrapper,
  carouselWrapper,
  loadingWrapperPosition,
  swiperSlideStyle
} from "../../DiscoveryJourneyModal.css";
import { Loading } from "../loading/Loading";
import { BenefitCard } from "./cards/BenefitCard";
import { baseTrackingValues } from "../../types";
import { anniversaryGiftCardContentPerMembership } from "./content/anniversary-gift-content";
import { welcomeGiftCardContentPerMembership } from "./content/welcome-gift.content";
import { freeShippingCardContent } from "./content/free-shipping-content";
import { samplesCardContent } from "./content/samples-content";

type CTAProps = {
  backToPreviousSlide?: () => void;
  goToNextSlide?: () => void;
  isActiveBenefitsLastSlide?: boolean;
  navigateToMemberBenefitsPage?: () => void;
};

export const ActiveBenefitsSlide: React.FC = () => {
  const { membership } = useUserV2();
  const { loading, activeBenefits } = useEntitlements();
  const currencySymbol = useCurrencySymbol();
  const { shippingThreshold, samplesThreshold } = useCampaignThresholds();
  const { ref } = useTrackElementViewed({
    ...baseTrackingValues,
    payload: { locationDetails: "active_benefits_slide" }
  });

  const transformActiveBenefits = [
    welcomeGiftCardContentPerMembership({
      welcomeGift: activeBenefits.welcomeGift,
      currentMembershipSku: membership?.id ? membership.id : ""
    }),
    anniversaryGiftCardContentPerMembership({
      anniversaryGift: activeBenefits.anniversaryGift,
      currentMembershipSku: membership?.id ? membership.id : ""
    }),
    // when we run free shipping weekends the card would confuse users
    ...(shippingThreshold !== 0
      ? [freeShippingCardContent({ currencySymbol, shippingThreshold })]
      : []),
    samplesCardContent({
      samples: activeBenefits.samples,
      samplesThreshold,
      currencySymbol,
      currentMembershipSku: membership?.id ? membership.id : ""
    })
  ];

  return (
    <div className={loading ? `${loadingWrapperPosition} ` : ""} ref={ref}>
      <div>
        <h2 className={bodyTitle}>Your Member Perks</h2>
        <p className={bodyDescription}>
          Discover your latest Beauty Pie Member Perks, all in one place
        </p>
      </div>
      {loading && <Loading message="Trying to fetch member perks" />}
      {!loading && (
        <Carousel
          carouselProps={{
            carouselWrapperClassName: carouselWrapper,
            showArrows: false,
            showScrollbar: true,
            gap: 0,
            itemsToShow: {
              [breakpointsInPx.md]: {
                slidesPerView: 1.5,
                slidesPerGroup: 1
              },
              [breakpointsInPx.sm]: {
                slidesPerView: 2.5,
                slidesPerGroup: 1
              },
              [breakpointsInPx.xs]: {
                slidesPerView: 2,
                slidesPerGroup: 2
              }
            }
          }}
        >
          {transformActiveBenefits.map((benefit) => {
            return (
              benefit && (
                <SwiperSlide
                  className={swiperSlideStyle}
                  key={`discovery-journey-active-benefit-${benefit.title}`}
                >
                  <BenefitCard
                    imageUrl={benefit.imageUrl}
                    title={benefit.title}
                    description={benefit.description}
                    imageStyle={activeBenefitsCardImageStyle}
                  />
                </SwiperSlide>
              )
            );
          })}
        </Carousel>
      )}
    </div>
  );
};

export const ActiveBenefitsSlideCTA: React.FC<CTAProps> = ({
  backToPreviousSlide,
  goToNextSlide,
  isActiveBenefitsLastSlide,
  navigateToMemberBenefitsPage
}) => {
  const { elementClicked } = useTrackElementClicked(baseTrackingValues);

  const nextButtonLabel = isActiveBenefitsLastSlide ? "View Perks" : "Continue";
  const contentUrl =
    isActiveBenefitsLastSlide &&
    (window ? `${window.location.origin}/account/member-benefits` : "");

  const nextButtonAction = () => {
    if (isActiveBenefitsLastSlide) {
      navigateToMemberBenefitsPage?.();
      elementClicked({
        details: "view_perks",
        locationDetails: "active_benefits_slide",
        ...(contentUrl && { contentUrl })
      });
    } else {
      goToNextSlide?.();
      elementClicked({
        details: "continue",
        locationDetails: "active_benefits_slide"
      });
    }
  };

  const previousButtonAction = () => {
    backToPreviousSlide?.();
    elementClicked({
      details: "back",
      locationDetails: "active_benefits_slide"
    });
  };

  return (
    <div className={buttonsWrapper}>
      <DeprecatedUISharedButton
        variant="tertiary"
        type="button"
        fullWidth
        ariaLabel="Back"
        handleClick={previousButtonAction}
      >
        Back
      </DeprecatedUISharedButton>{" "}
      <DeprecatedUISharedButton
        variant="primary"
        type="button"
        fullWidth
        ariaLabel={nextButtonLabel}
        handleClick={nextButtonAction}
      >
        {nextButtonLabel}
      </DeprecatedUISharedButton>
    </div>
  );
};
